import { runInAction, toJS } from 'mobx';

import type { ETACalculation } from '~/api/ETA';
import { ETADeliveryMethodType } from '~/api/ETADeliveryMethodType';

import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import { checkoutStore, mainStore, orderStore, userStore } from '~/stores';
import type { DeliveryAddress } from '~/stores/UserStore';

import { deliveryAddressStore } from './DeliveryAddressStore';
import type { EditAddressData } from './interface';

export function buildAddress2(
  bld?: string,
  floor?: string,
  apt?: string,
): string {
  const parts = [];

  if (bld) {
    parts.push(`Building: ${bld}`);
  }
  if (floor) {
    parts.push(`Floor: ${floor}`);
  }
  if (apt) {
    parts.push(`Apartment: ${apt}`);
  }

  return parts.join(', ');
}

export function sendAddressChangedAnalytics(
  deliveryAddress: DeliveryAddress,
  etaCalculation: ETACalculation | null,
) {
  analyticsService.track(AnalyticsEvent.GENERAL_ADDRESS_CHANGED, {
    postcode: deliveryAddress.zip || '',
    warehouse_code: etaCalculation?.warehouse.code || '',
    source: '',
    eta_min: etaCalculation?.duration.min || 0,
    eta_max: etaCalculation?.duration.max || 0,
    delivery_fee:
      etaCalculation?.cost[ETADeliveryMethodType.JiffyDelivery]
        ?.shippingPounds || 0,
    threshold:
      etaCalculation?.cost[ETADeliveryMethodType.JiffyDelivery]
        ?.thresholdPounds || 0,
    is_surger: etaCalculation?.highDemand || false,
    lat: deliveryAddress.coordinates.lat || '',
    lon: deliveryAddress.coordinates.lng || '',
  });
}

export function sendPostSaveAnalytics(deliveryAddress: DeliveryAddress) {
  mainStore.sendToRN('trackOneSignalOutcome', {
    name: 'warehouse_selected',
  });

  mainStore.sendToRN('setUserProperties', {
    'Address: postcode': deliveryAddress.zip || '',
  });

  mainStore.sendToRN('setUserProperties', {
    'Address: lat': deliveryAddress.coordinates.lat,
  });

  mainStore.sendToRN('setUserProperties', {
    'Address: lon': deliveryAddress.coordinates.lng,
  });
}

export function sendOnboardingAnalytics(
  deliveryAddress: DeliveryAddress,
  etaCalculation: ETACalculation | null,
) {
  const params = {
    warehouse_code: etaCalculation?.warehouse.code,
    postcode: deliveryAddress.zip || '',
    lat: deliveryAddress.coordinates.lat,
    lon: deliveryAddress.coordinates.lng,
  };

  analyticsService.track(AnalyticsEvent.ONBOARDING_WAREHOUSE_SELECTED, params);
}

export async function updateEtaAndDeliveryMethod(isPickup: boolean) {
  if (isPickup) return;

  const eta = deliveryAddressStore.etaCalculation;
  const currentMethod = checkoutStore.deliveryMethod;

  const availableMethods = eta?.warehouse.zone
    ? (Object.keys(eta.warehouse.zone) as ETADeliveryMethodType[])
    : [];

  const keepCurrentMethod = availableMethods.includes(currentMethod);
  const targetMethod = keepCurrentMethod
    ? currentMethod
    : deliveryAddressStore.getDeliveryMethodBySelectedZone() ||
      availableMethods[0];

  const methodWillChange = !!targetMethod && targetMethod !== currentMethod;

  // Batch both observable writes so depsHash is recomputed once and the 50ms
  // query-options delay coalesces the changes into a single calculate refetch.
  runInAction(() => {
    orderStore.setEtaCalculation(toJS(eta));
    if (methodWillChange && targetMethod) {
      checkoutStore.setDeliveryMethod(targetMethod);
    }
  });

  // When the method stays the same R11 does not fire — fetch ETA ourselves
  // so slots/prices refresh at save time.
  if (!methodWillChange) {
    await orderStore.requestETA();
  }
}

export async function saveAuthorizedAddress(
  deliveryAddress: DeliveryAddress,
  editAddressData: EditAddressData,
  options?: { alwaysSetDeliveryAddress?: boolean },
): Promise<{ addressId: string; updatedDeliveryAddress: DeliveryAddress }> {
  const effectiveAddressId =
    deliveryAddress.addressId || editAddressData.addressId || '';
  let addressId = effectiveAddressId;
  let updatedDeliveryAddress = deliveryAddress;

  if (effectiveAddressId) {
    const updatedAddress = await userStore.updateAddress(effectiveAddressId, {
      street_address_1: deliveryAddress.address1,
      street_address_2: editAddressData.address2 || deliveryAddress.address2,
      building: editAddressData.addressBld || deliveryAddress.addressBld,
      floor: editAddressData.addressFloor,
      apartment: editAddressData.addressApt,
      postcode: deliveryAddress.zip,
      latitude: deliveryAddress.coordinates.lat,
      longitude: deliveryAddress.coordinates.lng,
      type: editAddressData.type || deliveryAddress.type,
      comment: editAddressData.comment,
      instructions: editAddressData.instructions,
    });

    deliveryAddressStore.setEditAddressData({
      addressId: '',
      address2: '',
      addressFloor: '',
      addressBld: '',
      addressApt: '',
      type: 'home',
      comment: '',
      instructions: [],
    });

    await userStore.fetchAddresses();

    if (
      options?.alwaysSetDeliveryAddress ||
      userStore.deliveryAddress?.addressId === effectiveAddressId
    ) {
      const mergedAddress: DeliveryAddress = {
        ...deliveryAddress,
        ...editAddressData,
        addressId:
          editAddressData.addressId ?? deliveryAddress.addressId ?? null,
        address2:
          updatedAddress?.data.street_address_2 ??
          deliveryAddress.address2 ??
          editAddressData.address2,
        addressBld: updatedAddress?.data.building ?? '',
        addressFloor: updatedAddress?.data.floor ?? '',
        addressApt: updatedAddress?.data.apartment ?? '',
        comment: updatedAddress?.data.comment ?? '',
        instructions: updatedAddress?.data.instructions ?? [],
      };

      updatedDeliveryAddress = mergedAddress;
      // Batch observable writes so calculateDepsHash changes once.
      runInAction(() => {
        userStore.setDeliveryAddress(mergedAddress);
        checkoutStore.setAddress2Val(
          updatedAddress?.data.street_address_2 ??
            deliveryAddress.address2 ??
            editAddressData.address2,
        );
        checkoutStore.setCommentVal(updatedAddress?.data.comment ?? '');
      });
    }
  } else {
    const address = await userStore.addAddress({
      street_address_1: deliveryAddress.address1,
      street_address_2: editAddressData.address2 || deliveryAddress.address2,
      building: editAddressData.addressBld || deliveryAddress.addressBld,
      floor: editAddressData.addressFloor || deliveryAddress.addressFloor,
      apartment: editAddressData.addressApt || deliveryAddress.addressApt,
      city: deliveryAddress.city,
      postcode: deliveryAddress.zip,
      latitude: deliveryAddress.coordinates.lat,
      longitude: deliveryAddress.coordinates.lng,
      country: deliveryAddress.country,
      type: editAddressData.type || deliveryAddress.type,
      comment: editAddressData.comment,
      instructions: editAddressData.instructions,
    });
    addressId = address?.id || '';

    deliveryAddressStore.setEditAddressData({
      addressId: address?.id || '',
      address2: address?.street_address_2 || '',
      addressFloor: address?.floor || '',
      addressBld: address?.building || '',
      addressApt: address?.apartment || '',
      type: 'home',
      comment: '',
      instructions: address?.instructions || [],
    });

    await userStore.fetchAddresses();

    updatedDeliveryAddress = {
      ...deliveryAddress,
      addressId: address?.id || null,
      address2:
        address?.street_address_2 ??
        deliveryAddress.address2 ??
        editAddressData.address2,
      addressBld: address?.building ?? '',
      addressFloor: address?.floor ?? '',
      comment: address?.comment ?? '',
      instructions: address?.instructions ?? [],
    };

    // Batch observable writes so calculateDepsHash changes once.
    runInAction(() => {
      userStore.setDeliveryAddress({
        ...updatedDeliveryAddress,
        ...editAddressData,
        addressId:
          address?.id ??
          editAddressData.addressId ??
          deliveryAddress.addressId ??
          null,
        address2:
          address?.street_address_2 ??
          deliveryAddress.address2 ??
          editAddressData.address2,
        addressBld:
          address?.building ??
          (editAddressData.addressBld || deliveryAddress.addressBld),
      });
      checkoutStore.setAddress2Val(
        address?.street_address_2 ??
          deliveryAddress.address2 ??
          editAddressData.address2,
      );
      checkoutStore.setCommentVal(address?.comment ?? '');
    });
  }

  return { addressId, updatedDeliveryAddress };
}

export function saveUnauthorizedAddress(
  deliveryAddress: DeliveryAddress,
  editAddressData: EditAddressData,
) {
  const address2 = buildAddress2(
    editAddressData.addressBld,
    editAddressData.addressFloor,
    editAddressData.addressApt,
  );

  userStore.setDeliveryAddress({
    ...deliveryAddress,
    ...editAddressData,
    addressId: editAddressData.addressId ?? deliveryAddress.addressId ?? null,
    address2,
    addressBld: editAddressData.addressBld || deliveryAddress.addressBld,
  });
}
